import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Divider,
} from '@mui/material';
import ChatMessageList from './ChatMessageList';
import ChatInput from './ChatInput';
import ChatHeader from './ChatHeader';
import { useCurrentTranscript, useTranscript } from 'hooks/transcripts/queries.js';
import ConversationsAPI from 'chat/ConversationsAPI.js';
import useStreamHandler from 'utils/useStreamHandler';
import ConversationPickerModal from '../../../chat/ConversationPickerModal';
import { use } from 'react';
import { BOT_USER_ID } from 'constants/UserConstants';

const AIChatPanel = ({
  showHeader = true,
  onClose = null,
  height = '100%'
}) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [streamConfig, setStreamConfig] = useState({ url: null, headers: null });
  const [showConversationPicker, setShowConversationPicker] = useState(false);
  const { data: streamData, isComplete: streamComplete } = useStreamHandler(streamConfig.url, streamConfig.headers);
  const messagesEndRef = useRef(null);
  const currnentTranscript = useCurrentTranscript();
  const transcriptID = currnentTranscript?.transcriptID;
  const { data: transcript } = useTranscript(transcriptID);

  // Load existing conversation for this transcript and user on mount
  useEffect(() => {
    const loadExistingConversation = async () => {
      if (!transcript?.project_id || !transcriptID) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch conversations for this transcript, filtered by current user
        const response = await ConversationsAPI.getConversations(
          transcript.project_id,
          {
            users: 'current_user',
            transcript_id: transcriptID
          }
        );

        const conversations = response.data?.conversations || [];

        if (conversations.length > 0) {
          // Get the most recent conversation (they're ordered by created_at asc from API)
          const mostRecentConversation = conversations[conversations.length-1];

          // Load messages from this conversation
          const conversationResponse = await ConversationsAPI.getConversation(mostRecentConversation.id);
          const conversationData = conversationResponse.data || { messages: [] };
          const sortedMessages = conversationData.messages.sort((a, b) =>
            new Date(a.created_at) - new Date(b.created_at)
          );

          // Set the conversation ID and messages
          setConversationId(mostRecentConversation.id);
          setMessages(sortedMessages.map(msg => ({
            id: msg.id,
            content: msg.content,
            role: msg.user_id === BOT_USER_ID ? 'assistant' : 'user',
            timestamp: msg.created_at,
            isTyping: false
          })));
        } else {
          // No existing conversation, start with empty messages
          setMessages([]);
        }
      } catch (error) {
        console.error('Error loading existing conversation:', error);
        // Start with empty messages on error
        setMessages([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingConversation();
  }, [transcript?.project_id, transcriptID]);

  // Update messages with streaming data (same pattern as ProjectChatContainer)
  useEffect(() => {
    if (streamConfig.message_id && streamData) {
      // When first token arrives: stop loading, start streaming
      setIsLoading(false);
      setIsStreaming(true);

      setMessages(prevMessages => {
        const newMessages = [...prevMessages];
        const messageIndex = newMessages.findIndex(message => message.id === streamConfig.message_id);
        if (messageIndex > -1) {
          newMessages[messageIndex].content = streamData;
          newMessages[messageIndex].isTyping = false;
        }
        return newMessages;
      });
    }
  }, [streamData]);

  // Handle stream completion
  useEffect(() => {
    if (streamComplete) {
      setIsStreaming(false);
    }
  }, [streamComplete]);

  // Reset conversation when transcript changes
  useEffect(() => {
    setConversationId(null);
  }, [transcriptID])

  // Handle sending a new message
  const handleSendMessage = async (content) => {
    if (!content.trim()) return;

    const userMessage = {
      id: Date.now(),
      content: content.trim(),
      role: 'user',
      timestamp: new Date().toISOString(),
      isTyping: false
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Clear previous stream config to prevent old data flickering
    setStreamConfig({ url: null, headers: null, message_id: null });

    await handleTranscriptChat(content);
  };

  // Handle transcript chat with real API
  const handleTranscriptChat = async (content) => {
    try {
      let currentConversationId = conversationId;

      // Step 1: Check if we need to create a new conversation
      if (!currentConversationId) {
        // Create conversation for this transcript (only on first message)
        // Note: createTranscriptConversation already saves the first message
        const conversationResponse = await ConversationsAPI.createTranscriptConversation(
          transcriptID,
          content
        );
        currentConversationId = conversationResponse.data.id;
        setConversationId(currentConversationId);
      } else {
        // Add message to existing conversation
        await ConversationsAPI.createMessage(currentConversationId, content);
      }

      // Step 2: Create AI response with transcript context
      const aiResponse = await ConversationsAPI.createAIConversation(
        currentConversationId,
        [], // empty code_ids for transcript chat
        null, // no op needed
        [transcriptID] // transcript context
      );

      // Step 4: Set up streaming
      const data = aiResponse.data || {};
      const message = data.message || {};
      const stream = data.stream || {};

      // Add AI message placeholder
      const aiMessage = {
        id: message.id || Date.now() + 1,
        content: '',
        role: 'assistant',
        timestamp: new Date().toISOString(),
        isTyping: true
      };
      setMessages(prev => [...prev, aiMessage]);

      setStreamConfig({
        url: stream.stream_url,
        headers: data.auth,
        message_id: message.id
      });

      // Keep isLoading true until first token arrives (handled in streamData useEffect)

    } catch (error) {
      console.error('Transcript chat error:', error);
      // Check if this is a trial limit error
      if (error.response?.status === 402 && error.response?.data?.error === 'trial_limit_reached') {
        const errorMessage = {
          id: Date.now() + 1,
          content: `You've reached the chat limit for your free trial. To continue chatting, [please upgrade to a paid subscription.](/users/upgrade)`,
          role: 'assistant',
          timestamp: new Date().toISOString(),
          isTyping: false,
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
      } else {
        // Add generic error message
        const errorMessage = {
          id: Date.now() + 1,
          content: "I'm sorry, I encountered an error. Please try again.",
          role: 'assistant',
          timestamp: new Date().toISOString(),
          isTyping: false,
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
      }
      setIsLoading(false);
      setIsStreaming(false);
    }
  };


  // Clear chat history
  const handleClearChat = () => {
    setMessages([]);
    setConversationId(null); // Reset conversation to start fresh
  };

  // Show all chats modal
  const handleShowAllChats = () => {
    setShowConversationPicker(true);
  };

  // Load a selected conversation from the modal
  const handleConversationSelect = async (conversationId) => {
    try {
      setIsLoading(true);

      // Fetch the conversation messages
      const conversationResponse = await ConversationsAPI.getConversation(conversationId);
      const conversationData = conversationResponse.data || { messages: [] };
      const sortedMessages = conversationData.messages.sort((a, b) =>
        new Date(a.created_at) - new Date(b.created_at)
      );

      // Set the conversation ID and messages
      setConversationId(conversationId);
      setMessages(sortedMessages.map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.user_id === BOT_USER_ID ? 'assistant' : 'user',
        timestamp: msg.created_at,
        isTyping: false
      })));

      // Close the modal
      setShowConversationPicker(false);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading conversation:', error);
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: height,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        fontSize: '1rem'
      }}
    >
      {/* Header */}
      {showHeader && (
        <>
          <ChatHeader
            title={transcript?.name || 'your transcript'}
            onClose={onClose}
            onClearChat={handleClearChat}
            onShowAllChats={handleShowAllChats}
            isLoading={isLoading}
            isStreaming={isStreaming}
          />
          <Divider />
        </>
      )}

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <ChatMessageList
          messages={messages}
          isLoading={isLoading}
          messagesEndRef={messagesEndRef}
          transcriptId={transcriptID}
          transcriptName={transcript?.name}
        />
      </Box>

      <Divider />

      {/* Input Area */}
      <ChatInput
        onSendMessage={handleSendMessage}
        disabled={isLoading || isStreaming}
        placeholder="Ask a follow up question."
      />

      <ConversationPickerModal
        show={showConversationPicker}
        transcriptID={transcriptID}
        users="current_user"
        projectId={transcript?.project_id}
        onHide={() => setShowConversationPicker(false)}
        onConversationClick={handleConversationSelect}
      />
    </Box>
  );
};

export default AIChatPanel;